import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Plus,
  Play,
  Pause,
  CheckCircle,
  Users,
  DollarSign,
  TrendingUp,
  MoreVertical,
  Edit,
  Eye,
  Settings,
  AlertCircle
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const EventsList = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const queryClient = useQueryClient();

  const { data: eventsData, isLoading, error } = useQuery(
    ['events', { status: statusFilter }],
    () => api.get('/events', {
      params: { status: statusFilter === 'all' ? undefined : statusFilter }
    }).then(res => res.data),
    {
      staleTime: 30000
    }
  );

  const updateStatusMutation = useMutation(
    ({ eventId, status }) => api.put(`/events/${eventId}`, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('events');
        queryClient.invalidateQueries('admin-dashboard');
        toast.success('Estado del evento actualizado');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al actualizar evento');
      }
    }
  );

  const handleStatusChange = (eventId, newStatus) => {
    updateStatusMutation.mutate({ eventId, status: newStatus });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: 'success',
        icon: Play,
        label: 'Activo',
        bgClass: 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300'
      },
      paused: {
        color: 'warning',
        icon: Pause,
        label: 'Pausado',
        bgClass: 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-300'
      },
      completed: {
        color: 'primary',
        icon: CheckCircle,
        label: 'Completado',
        bgClass: 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300'
      }
    };

    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgClass}`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `$${Number(amount || 0).toLocaleString()}`;
  };

  const DropdownMenu = ({ event, onClose }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95, y: -10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      className="absolute right-0 top-8 w-48 bg-white dark:bg-dark-800 rounded-lg shadow-lg border border-gray-200 dark:border-dark-700 py-1 z-20"
    >
      <Link
        to={`/admin/events/${event.id}`}
        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700"
        onClick={onClose}
      >
        <Eye className="w-4 h-4 mr-3" />
        Ver detalles
      </Link>

      <Link
        to={`/admin/events/${event.id}/edit`}
        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700"
        onClick={onClose}
      >
        <Edit className="w-4 h-4 mr-3" />
        Editar evento
      </Link>

      <hr className="my-1 border-gray-200 dark:border-dark-700" />

      {event.status === 'active' && (
        <button
          onClick={() => {
            handleStatusChange(event.id, 'paused');
            onClose();
          }}
          className="flex items-center w-full px-4 py-2 text-sm text-warning-600 dark:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20"
        >
          <Pause className="w-4 h-4 mr-3" />
          Pausar evento
        </button>
      )}

      {event.status === 'paused' && (
        <button
          onClick={() => {
            handleStatusChange(event.id, 'active');
            onClose();
          }}
          className="flex items-center w-full px-4 py-2 text-sm text-success-600 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20"
        >
          <Play className="w-4 h-4 mr-3" />
          Reanudar evento
        </button>
      )}

      {(event.status === 'active' || event.status === 'paused') && (
        <button
          onClick={() => {
            handleStatusChange(event.id, 'completed');
            onClose();
          }}
          className="flex items-center w-full px-4 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
        >
          <CheckCircle className="w-4 h-4 mr-3" />
          Marcar como completado
        </button>
      )}
    </motion.div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8 text-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Error al cargar eventos
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {error.response?.data?.error || 'Error de conexión'}
        </p>
      </div>
    );
  }

  const { events = [] } = eventsData || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Gestión de Eventos
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Administra los eventos y ciclos de apuestas
          </p>
        </div>

        <Link
          to="/admin/events/new"
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Evento
        </Link>
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Filtrar por estado:
            </span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input-field"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="paused">Pausados</option>
              <option value="completed">Completados</option>
            </select>
          </div>

          <div className="text-sm text-gray-600 dark:text-gray-400">
            {events.length} evento{events.length !== 1 ? 's' : ''} encontrado{events.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No hay eventos
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {statusFilter === 'all'
              ? 'Aún no has creado ningún evento'
              : `No hay eventos con estado "${statusFilter}"`
            }
          </p>
          {statusFilter === 'all' && (
            <Link to="/admin/events/new" className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Crear primer evento
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {events.map((event, index) => (
            <motion.div
              key={event.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="card-hover p-6 relative"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {event.name}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {event.description || 'Sin descripción'}
                  </p>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  {getStatusBadge(event.status)}

                  <div className="relative">
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === event.id ? null : event.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>

                    <AnimatePresence>
                      {activeDropdown === event.id && (
                        <DropdownMenu
                          event={event}
                          onClose={() => setActiveDropdown(null)}
                        />
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {event.status === 'active' && (
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600 dark:text-gray-400">Progreso</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      Paso {event.current_step} / {event.max_steps || 13}
                    </span>
                  </div>

                  <div className="w-full bg-gray-200 dark:bg-dark-700 rounded-full h-2">
                    <div
                      className="bg-gradient-primary h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${(event.current_step / (event.max_steps || 13)) * 100}%`
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-center mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      event.current_step <= 5
                        ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                        : event.current_step <= 9
                        ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300'
                        : 'bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300'
                    }`}>
                      {event.current_step <= 5 ? '🟢 Riesgo Bajo' :
                       event.current_step <= 9 ? '🟡 Riesgo Medio' :
                       '🔴 Riesgo Alto'}
                    </span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <Users className="w-5 h-5 text-primary-600 dark:text-primary-400 mx-auto mb-1" />
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {event.participant_count || 0}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Participantes
                  </p>
                </div>

                <div className="text-center">
                  <DollarSign className="w-5 h-5 text-success-600 dark:text-success-400 mx-auto mb-1" />
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {formatCurrency(event.total_capital_managed)}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Capital
                  </p>
                </div>

                <div className="text-center">
                  <TrendingUp className="w-5 h-5 text-warning-600 dark:text-warning-400 mx-auto mb-1" />
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {event.status === 'active' ? event.current_step : '-'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Paso Actual
                  </p>
                </div>
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <p>
                  <strong>Inicio:</strong> {formatDate(event.start_date)}
                </p>
                {event.end_date && (
                  <p>
                    <strong>Fin:</strong> {formatDate(event.end_date)}
                  </p>
                )}
                <p>
                  <strong>Creado:</strong> {formatDate(event.created_at)}
                </p>
              </div>

              <div className="flex space-x-2 mt-4">
                <Link
                  to={`/admin/events/${event.id}`}
                  className="flex-1 btn-secondary text-sm py-2"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  Ver
                </Link>

                {event.status === 'active' && (
                  <Link
                    to={`/admin/betting?event=${event.id}`}
                    className="flex-1 btn-primary text-sm py-2"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Gestionar
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {activeDropdown && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </div>
  );
};

export default EventsList;